import { Fragment, FunctionalComponent, h } from "preact";
import { useState } from "preact/hooks";
import { sendFeedbackAnalyticsEventForRibbon } from "@shared/Analytics/content/feedback-analytics";
import { IceButtonWithLoading } from "@shared/Components/IceButtons/IceButtonWithLoading";
import clsx from "clsx";
import { sendFeedbackText } from "../../Shared/feedback.service";
import { IceFeedbackFormSteps } from "../../Shared/feedback.models";
import { FEEDBACK_LABEL, ERROR_MESSAGE } from "../../Shared/feedback.constants";
import { FeedbackText } from "../../Shared/feedback-text";

export interface IceFeedbackFormProps {
  userOpinionState: boolean | undefined;
  feedbackFormStep: IceFeedbackFormSteps;
  setFeedbackFormStep: (feedbackStep: number) => void;
  publicId: string;
}

export const IceFeedbackForm: FunctionalComponent<IceFeedbackFormProps> = ({
  userOpinionState,
  feedbackFormStep,
  setFeedbackFormStep,
  publicId,
}: IceFeedbackFormProps) => {
  const [feedbackFormText, setFeedbackFormText] = useState("");
  const [formState, setFormState] = useState<"initial" | "loading" | "invalid" | "disabled" | "error">("initial");

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (feedbackFormText.trim().length < 2) {
      sendFeedbackAnalyticsEventForRibbon(userOpinionState ? "ja_send" : "nei_send", "feedback_question_2");
      setFeedbackFormStep(feedbackFormStep + 1);
      return;
    }

    setFormState("loading");
    try {
      const response = await sendFeedbackText(publicId, feedbackFormText);

      if (response.ok) {
        setFeedbackFormStep(feedbackFormStep + 1);
        sendFeedbackAnalyticsEventForRibbon(
          userOpinionState ? "ja-kommentar_send" : "nei-kommentar_send",
          "feedback_question_2",
        );
      } else {
        setFormState("error");
      }
    } catch (error) {
      console.error(error);
      setFormState("error");
    }
  };

  return (
    <Fragment>
      <div className="ice-feedback__title">
        <FeedbackText isPositive={userOpinionState === true} />
      </div>
      <form className="ice-feedback-form" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="feedbackFormText">{FEEDBACK_LABEL}</label>
          <textarea
            value={feedbackFormText}
            onChange={(e) => {
              setFeedbackFormText((e.target as HTMLTextAreaElement).value);
            }}
            type="text"
            name="feedbackFormText"
            id="feedbackFormText"
          ></textarea>
        </div>
        <div className="feedback--error" aria-label="feilmelding" aria-live="polite">
          {formState === "error" && <p>{ERROR_MESSAGE}</p>}
        </div>
        <div className="system-message system-message__warning">
          <div className="system-message__icon">
            <svg style="width:4.8em;height:4.8em" focusable="false" aria-hidden="true">
              <use xlinkHref="#ice-hand-point--up"></use>
            </svg>
          </div>
          <div className="system-message__text">
            <p>
              Viktig! Dette er en ubetjent kanal, så ikke del telefonnummer eller annen personlig info her.
              <br />
              Trenger du hjelp? Ta kontakt med{" "}
              <strong>
                <a className="link" href="https://www.ice.no/kundeservice/kontakt-oss/">
                  kundeservice
                </a>
              </strong>
              , eller{" "}
              <strong>
                <a className="link" href="/kundeservice/kontakt-oss/kontaktskjema/">
                  fyll ut kontaktskjemaet vårt
                </a>
              </strong>{" "}
              – så hører du fra oss!
            </p>
          </div>
        </div>
        <div className={`ice-feedback-form__footer`}>
          <div>
            <button
              className="btn--feedback btn--feedback__back"
              onClick={() => {
                setFeedbackFormStep(feedbackFormStep - 1);
                sendFeedbackAnalyticsEventForRibbon(
                  userOpinionState ? "back_to_question_1_yes" : "back_to_question_1_no",
                  "feedback_question_2",
                );
              }}
            >
              <div>
                <div>
                  <IceSvgByName name="ice-arrow--back" />
                </div>
                <div>
                  <span>Tilbake</span>
                </div>
              </div>
            </button>
          </div>
          <IceButtonWithLoading type="submit" className="btn--feedback__submit" btnState={formState}>
            <div className={clsx("btn-content", formState === "loading" ? "visibility-hidden" : "")}>Send inn</div>
          </IceButtonWithLoading>
        </div>
      </form>
    </Fragment>
  );
};

export const IceSvgByName: FunctionalComponent<{ name: string }> = ({ name }: { name: string }) => {
  return (
    <svg style="width:4.8em;height:4.8em" focusable="false" aria-hidden="true">
      <use xlinkHref={`#${name}`}></use>
    </svg>
  );
};

export const IceFeedbackPopupFormSuccess = () => {
  return (
    <div className="ice-feedback-form--success m-2" role="region" aria-labelledby="ice-form-success">
      <div>
        <svg width="74" height="74" viewBox="0 0 74 74" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M18.466 26.2583C16.9896 25.869 15.7169 26.4652 15.7155 27.2585C15.7141 28.0518 15.6281 33.3463 15.6099 44.3699C15.6088 45.0367 14.9079 45.5773 14.0419 45.578L14.2694 26.8819L14.2713 25.7512L18.0806 25.7419L18.4643 26.0862L18.466 26.2583Z"
            fill="#FFCD00"
          />
          <path
            d="M23.2982 29.4069C21.9825 29.0088 20.85 29.581 20.8491 30.3902C20.8484 31.1297 20.823 34.958 20.8094 44.2224C20.8083 44.978 20.0765 45.5921 19.172 45.5926L19.1942 30.0472L19.1954 28.8876L22.9542 28.8796L23.2965 29.2312L23.2982 29.4069Z"
            fill="#FFCD00"
          />
          <path
            d="M12.9543 30.8438C11.8079 30.4529 10.5752 30.6715 10.5753 31.4701C10.5753 32.0978 10.5613 37.4465 10.5758 45.6965C10.5774 46.5786 9.84039 47.2919 8.92808 47.292L9.38027 31.4701L9.38017 30.333L12.6542 30.3265L12.9526 30.6715L12.9543 30.8438Z"
            fill="#FFCD00"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.3709 25.4002C13.7489 26.0395 13.3769 26.9664 13.3769 28.1134V29.6844C13.3002 29.6255 13.2197 29.5707 13.1352 29.52C12.34 29.0429 11.1702 28.8706 10.1678 29.2105C9.65171 29.3854 9.16254 29.7028 8.80511 30.202C8.44678 30.7025 8.25384 31.3397 8.25384 32.0981V35.552C8.20368 35.5233 8.15362 35.4966 8.10394 35.4717C7.8847 35.3621 7.54654 35.2556 7.17229 35.1984C6.79327 35.1405 6.32343 35.1247 5.85394 35.2421C5.37443 35.362 4.88537 35.6248 4.52439 36.1212C4.16756 36.6118 3.98461 37.2636 3.98461 38.075V54.5827C3.98461 57.2734 4.59065 59.1139 5.80703 60.9385C6.46232 61.9214 7.63673 62.8416 8.90193 63.5114C10.1702 64.1828 11.6339 64.6581 12.9215 64.6581H17.4754C20.8187 64.6581 23.5234 63.1373 25.557 61.6847C27.6517 60.1885 29.7193 57.8153 31.1895 54.5807C32.6137 51.4475 33.7647 48.8591 34.9149 45.409C35.1493 44.7234 35.1744 44.0408 34.8952 43.4171C34.6193 42.8007 34.093 42.347 33.4347 42.0179C32.7724 41.6867 31.9822 41.4972 31.1961 41.636C30.3852 41.7791 29.6508 42.26 29.1281 43.1312C28.6783 43.881 27.9085 45.515 27.2161 46.9948C27.161 47.1126 27.1063 47.2295 27.0522 47.3453C26.7609 47.9684 26.4856 48.5572 26.2525 49.0428C26.035 49.496 25.8709 49.8232 25.7701 50.006C25.5308 49.5261 25.2604 48.5413 25.0473 47.4757C24.8359 46.4184 24.7048 45.4051 24.7047 44.9057C24.7045 43.1574 24.7046 41.5418 24.7046 37.8871C24.7046 36.1869 24.7046 34.0455 24.7046 31.2442C24.7046 30.43 24.4532 29.5754 23.9701 28.9079C23.4797 28.2304 22.721 27.715 21.7447 27.715C21.0055 27.715 20.3702 27.8563 19.8616 28.1693C19.7699 25.8607 18.2904 24.5236 16.6405 24.4777C15.816 24.4548 14.989 24.7649 14.3709 25.4002ZM18.5 44.9058C18.5 45.283 18.8058 45.5888 19.1831 45.5888C19.5603 45.5888 19.8661 45.283 19.8661 44.9058V31.5288C19.8661 30.566 20.0106 29.9707 20.2676 29.6203C20.4874 29.3207 20.8816 29.0812 21.7448 29.0812C22.1915 29.0811 22.5713 29.3053 22.8634 29.709C23.163 30.1228 23.3385 30.6913 23.3385 31.2442C23.3385 34.041 23.3384 36.18 23.3384 37.8789C23.3384 41.5394 23.3384 43.1562 23.3386 44.9058C23.3387 45.5449 23.4922 46.6662 23.7077 47.7436C23.9212 48.811 24.2195 49.9596 24.5492 50.6189C24.6886 50.8979 24.8503 51.1433 25.0522 51.3196C25.2704 51.5101 25.5589 51.6362 25.8881 51.5929C26.1851 51.5539 26.3982 51.3906 26.5242 51.2701C26.6546 51.1453 26.7562 51.0035 26.8253 50.9011C26.9724 50.6827 27.2157 50.1933 27.4842 49.6339C27.7205 49.1415 27.9994 48.5451 28.2905 47.9224C28.3445 47.8069 28.3989 47.6906 28.4535 47.5738C29.1627 46.058 29.8956 44.5075 30.2996 43.8341C30.6307 43.2823 31.0347 43.0517 31.4335 42.9813C31.857 42.9066 32.3476 43.0017 32.8237 43.2398C33.3039 43.4799 33.542 43.7378 33.6483 43.9752C33.7509 44.2044 33.7765 44.5169 33.6215 44.9688L33.6196 44.9744C32.4932 48.3537 31.3674 50.8879 29.9458 54.0154C28.5699 57.0424 26.6529 59.223 24.763 60.573C22.812 61.9665 20.3936 63.2919 17.4754 63.2919H12.9215C11.9322 63.2919 10.6921 62.9133 9.54114 62.304C8.38711 61.693 7.42691 60.9055 6.94373 60.1807C5.88319 58.5899 5.35077 57.0151 5.35077 54.5827V38.075C5.35077 37.4633 5.48801 37.1189 5.62925 36.9247C5.76635 36.7362 5.95325 36.6255 6.18528 36.5675C6.42733 36.507 6.70461 36.509 6.96597 36.5489C7.2321 36.5896 7.4276 36.661 7.49298 36.6936C7.69656 36.7954 7.87936 36.9374 8.01279 37.1375C8.14338 37.3334 8.25384 37.6253 8.25384 38.075V46.6135C8.25384 46.9907 8.55967 47.2965 8.93692 47.2965C9.31417 47.2965 9.62 46.9907 9.62 46.6135V32.0981C9.62 31.5757 9.75015 31.2288 9.91592 30.9973C10.0826 30.7645 10.3185 30.6019 10.6064 30.5043C11.2119 30.299 11.9655 30.4114 12.4324 30.6915C12.9447 30.9989 13.3769 31.8345 13.3769 33.8058V44.9058C13.3769 45.283 13.6827 45.5888 14.06 45.5888C14.4373 45.5888 14.7431 45.283 14.7431 44.9058V28.1134C14.7431 27.2682 15.0115 26.7009 15.3501 26.3529C15.6925 26.0009 16.1463 25.8307 16.6026 25.8434C17.4604 25.8672 18.5 26.5777 18.5 28.3981V44.9058Z"
            fill="black"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M26.2286 55.3495L25.8666 55.9287C25.5762 56.3933 24.967 57.0991 24.1768 57.8174C23.3915 58.5312 22.4717 59.217 21.5838 59.6658C19.5978 60.6696 17.5842 60.7308 15.9151 60.7308L15.232 60.7308L15.232 62.0969L15.9151 62.0969C17.6029 62.0969 19.9031 62.046 22.2001 60.885C23.231 60.364 24.2518 59.5954 25.0957 58.8284C25.9346 58.0658 26.6429 57.2643 27.0251 56.6528L27.3871 56.0736L26.2286 55.3495Z"
            fill="#AACD87"
          />
          <path
            d="M56.0223 26.2583C57.4987 25.869 58.7714 26.4652 58.7728 27.2585C58.7742 28.0518 58.8602 33.3463 58.8784 44.3699C58.8795 45.0367 59.5804 45.5773 60.4464 45.578L60.2189 26.8819L60.2169 25.7512L56.4076 25.7419L56.0239 26.0862L56.0223 26.2583Z"
            fill="#FFCD00"
          />
          <path
            d="M51.1901 29.4069C52.5058 29.0088 53.6383 29.581 53.6391 30.3902C53.6399 31.1297 53.6652 34.958 53.6789 44.2224C53.68 44.978 54.4118 45.5921 55.3163 45.5926L55.294 30.0472L55.2929 28.8876L51.534 28.8796L51.1918 29.2312L51.1901 29.4069Z"
            fill="#FFCD00"
          />
          <path
            d="M61.534 30.8438C62.6804 30.4529 63.9131 30.6715 63.913 31.4701C63.913 32.0978 63.927 37.4465 63.9125 45.6965C63.9109 46.5786 64.6479 47.2919 65.5602 47.292L65.108 31.4701L65.1081 30.333L61.8341 30.3265L61.5356 30.6715L61.534 30.8438Z"
            fill="#FFCD00"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M60.1174 25.4002C60.7394 26.0395 61.1114 26.9664 61.1114 28.1134V29.6844C61.1881 29.6255 61.2686 29.5707 61.353 29.52C62.1483 29.0429 63.318 28.8706 64.3205 29.2105C64.8366 29.3854 65.3257 29.7028 65.6832 30.202C66.0415 30.7025 66.2344 31.3397 66.2344 32.0981V35.552C66.2846 35.5233 66.3347 35.4966 66.3843 35.4717C66.6036 35.3621 66.9417 35.2556 67.316 35.1984C67.695 35.1405 68.1649 35.1247 68.6343 35.2421C69.1138 35.362 69.6029 35.6248 69.9639 36.1212C70.3207 36.6118 70.5037 37.2636 70.5037 38.075V54.5827C70.5037 57.2734 69.8976 59.1139 68.6813 60.9385C68.026 61.9214 66.8515 62.8416 65.5863 63.5114C64.3181 64.1828 62.8543 64.6581 61.5667 64.6581H57.0129C53.6696 64.6581 50.9649 63.1373 48.9313 61.6847C46.8366 60.1885 44.769 57.8153 43.2987 54.5807C41.8745 51.4475 40.7236 48.8591 39.5734 45.409C39.339 44.7234 39.3139 44.0408 39.5931 43.4171C39.869 42.8007 40.3953 42.347 41.0536 42.0179C41.7159 41.6867 42.5061 41.4972 43.2922 41.636C44.103 41.7791 44.8374 42.26 45.3602 43.1312C45.81 43.881 46.5798 45.515 47.2722 46.9948C47.3273 47.1126 47.3819 47.2295 47.4361 47.3453C47.7274 47.9684 48.0027 48.5572 48.2358 49.0428C48.4533 49.496 48.6174 49.8232 48.7182 50.006C48.9575 49.5261 49.2279 48.5413 49.441 47.4757C49.6524 46.4184 49.7835 45.4051 49.7835 44.9057C49.7837 43.1574 49.7837 41.5418 49.7837 37.8871C49.7837 36.1869 49.7837 34.0455 49.7837 31.2442C49.7837 30.43 50.0351 29.5754 50.5181 28.9079C51.0086 28.2304 51.7673 27.715 52.7435 27.715C53.4828 27.715 54.1181 27.8563 54.6266 28.1693C54.7184 25.8607 56.1979 24.5236 57.8478 24.4777C58.6723 24.4548 59.4993 24.7649 60.1174 25.4002ZM55.9883 44.9058C55.9883 45.283 55.6825 45.5888 55.3052 45.5888C54.928 45.5888 54.6221 45.283 54.6221 44.9058V31.5288C54.6221 30.566 54.4777 29.9707 54.2207 29.6203C54.0009 29.3207 53.6067 29.0812 52.7435 29.0812C52.2967 29.0811 51.917 29.3053 51.6248 29.709C51.3253 30.1228 51.1498 30.6913 51.1498 31.2442C51.1498 34.041 51.1498 36.18 51.1499 37.8789C51.1499 41.5394 51.1499 43.1562 51.1497 44.9058C51.1496 45.5449 50.9961 46.6662 50.7806 47.7436C50.5671 48.811 50.2688 49.9596 49.9391 50.6189C49.7996 50.8979 49.638 51.1433 49.436 51.3196C49.2179 51.5101 48.9294 51.6362 48.6002 51.5929C48.3032 51.5539 48.0901 51.3906 47.9641 51.2701C47.8336 51.1453 47.732 51.0035 47.663 50.9011C47.5159 50.6827 47.2726 50.1933 47.0041 49.6339C46.7678 49.1415 46.4889 48.5451 46.1978 47.9224C46.1438 47.8069 46.0894 47.6906 46.0348 47.5738C45.3256 46.058 44.5927 44.5075 44.1887 43.8341C43.8576 43.2823 43.4535 43.0517 43.0548 42.9813C42.6312 42.9066 42.1407 43.0017 41.6645 43.2398C41.1844 43.4799 40.9463 43.7378 40.84 43.9752C40.7374 44.2044 40.7118 44.5169 40.8667 44.9688L40.8686 44.9744C41.9951 48.3537 43.1209 50.8879 44.5424 54.0154C45.9183 57.0424 47.8354 59.223 49.7253 60.573C51.6763 61.9665 54.0947 63.2919 57.0129 63.2919H61.5667C62.5561 63.2919 63.7962 62.9133 64.9471 62.304C66.1012 61.693 67.0614 60.9055 67.5445 60.1807C68.6051 58.5899 69.1375 57.0151 69.1375 54.5827V38.075C69.1375 37.4633 69.0003 37.1189 68.859 36.9247C68.7219 36.7362 68.535 36.6255 68.303 36.5675C68.0609 36.507 67.7837 36.509 67.5223 36.5489C67.2562 36.5896 67.0607 36.661 66.9953 36.6936C66.7917 36.7954 66.6089 36.9374 66.4755 37.1375C66.3449 37.3334 66.2344 37.6253 66.2344 38.075V46.6135C66.2344 46.9907 65.9286 47.2965 65.5514 47.2965C65.1741 47.2965 64.8683 46.9907 64.8683 46.6135V32.0981C64.8683 31.5757 64.7381 31.2288 64.5724 30.9973C64.4057 30.7645 64.1698 30.6019 63.8819 30.5043C63.2764 30.299 62.5228 30.4114 62.0559 30.6915C61.5436 30.9989 61.1114 31.8345 61.1114 33.8058V44.9058C61.1114 45.283 60.8055 45.5888 60.4283 45.5888C60.051 45.5888 59.7452 45.283 59.7452 44.9058V28.1134C59.7452 27.2682 59.4768 26.7009 59.1382 26.3529C58.7958 26.0009 58.3419 25.8307 57.8857 25.8434C57.0278 25.8672 55.9883 26.5777 55.9883 28.3981V44.9058Z"
            fill="black"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M48.2597 55.3495L48.6217 55.9287C48.9121 56.3933 49.5213 57.0991 50.3115 57.8174C51.0967 58.5312 52.0166 59.217 52.9044 59.6658C54.8905 60.6696 56.9041 60.7308 58.5732 60.7308L59.2563 60.7308L59.2563 62.0969L58.5732 62.0969C56.8854 62.0969 54.5852 62.046 52.2882 60.885C51.2573 60.364 50.2364 59.5954 49.3926 58.8284C48.5537 58.0658 47.8454 57.2643 47.4632 56.6528L47.1012 56.0736L48.2597 55.3495Z"
            fill="#AACD87"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M37.6401 17.9873C37.2629 17.9873 36.957 17.6815 36.957 17.3042L36.957 9.90423C36.957 9.52697 37.2629 9.22115 37.6401 9.22115C38.0174 9.22115 38.3232 9.52697 38.3232 9.90423L38.3232 17.3042C38.3232 17.6815 38.0174 17.9873 37.6401 17.9873Z"
            fill="#FFCD00"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M43.2317 22.4253C42.9117 22.2255 42.8143 21.8041 43.0141 21.4841L46.0441 16.6317C46.244 16.3117 46.6653 16.2143 46.9853 16.4141C47.3053 16.6139 47.4027 17.0353 47.2029 17.3553L44.1729 22.2077C43.9731 22.5277 43.5517 22.6251 43.2317 22.4253Z"
            fill="#FFCD00"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M31.6159 22.7358C31.9359 22.536 32.0333 22.1146 31.8335 21.7946L28.8035 16.9423C28.6037 16.6223 28.1823 16.5248 27.8623 16.7247C27.5423 16.9245 27.4449 17.3459 27.6447 17.6659L30.6747 22.5182C30.8745 22.8382 31.2959 22.9356 31.6159 22.7358Z"
            fill="#FFCD00"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M36.8003 17.3047C36.423 17.3047 36.1172 16.9989 36.1172 16.6216L36.1172 9.22161C36.1172 8.84436 36.423 8.53853 36.8003 8.53853C37.1775 8.53853 37.4833 8.84436 37.4833 9.22161L37.4833 16.6216C37.4833 16.9989 37.1775 17.3047 36.8003 17.3047Z"
            fill="black"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M42.7005 22.0532C42.3805 21.8534 42.2831 21.432 42.4829 21.112L45.5129 16.2596C45.7127 15.9397 46.1341 15.8422 46.4541 16.042C46.7741 16.2419 46.8715 16.6632 46.6717 16.9832L43.6417 21.8356C43.4419 22.1556 43.0205 22.253 42.7005 22.0532Z"
            fill="black"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M30.5183 22.0532C30.8383 21.8534 30.9357 21.432 30.7359 21.112L27.7059 16.2596C27.506 15.9397 27.0847 15.8422 26.7647 16.042C26.4447 16.2419 26.3473 16.6632 26.5471 16.9832L29.5771 21.8356C29.7769 22.1556 30.1983 22.253 30.5183 22.0532Z"
            fill="black"
          />
        </svg>
      </div>
      <div className="sid-headline-5 pb-1">Mottatt!</div>
      <div className="text-body--bigger">
        <p id="ice-form-success">Tusen takk for dine innspill!</p>
      </div>
    </div>
  );
};
